import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pagination, useTheme } from "@mui/material";
import { isMobile } from "@app/helpers";
import { CURRENT_PAGE, LIMIT_PAGE, TOTAL_PAGE } from "@app/constants";

type PaginationProps = {
  limitPage?: number;
  totalPage?: number;
  currentPage?: number;
  handleChangePage?: (value: number) => void;
  className?: string;
  isHidden?: boolean;
};

export function DataPagination({
  limitPage = LIMIT_PAGE,
  totalPage = TOTAL_PAGE,
  currentPage = CURRENT_PAGE,
  handleChangePage = () => {},
  className = "",
  isHidden = false,
}: PaginationProps) {
  const [page, setPage] = useState(currentPage);
  const count = totalPage && limitPage && Math.ceil(totalPage / limitPage);
  const { t } = useTranslation();
  const theme = useTheme();
  const [siblingCount, setSiblingCount] = useState(isMobile() ? 0 : 1);

  const isRTL = theme.direction === "rtl";
  const getPaginationTotalText = () => {
    const firstRecordNumOfCurrentPage = (currentPage - 1) * limitPage + 1;
    const lastRecordNumOfCurrentPage =
      limitPage * currentPage < totalPage ? limitPage * currentPage : totalPage;
    const recordLabel =
      totalPage === 1 ? t("record.count_one") : t("record.count_other");

    if (isRTL) {
      return `${recordLabel} ${totalPage} / ${lastRecordNumOfCurrentPage} ~ ${firstRecordNumOfCurrentPage}`;
    }

    return `${firstRecordNumOfCurrentPage} ~ ${lastRecordNumOfCurrentPage} / ${totalPage} ${recordLabel}`;
  };

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  return (
    <>
      {!isHidden && (
        <div
          className={`data-pagination flex-centered justify-content-between ${className}`}
        >
          <div className="pagination-total-text">
            {getPaginationTotalText()}
          </div>
          <Pagination
            className={`pagination ${
              page === 3 && count > 7 ? "has-page3" : ""
            }`}
            count={count}
            variant="outlined"
            shape="rounded"
            page={page}
            onChange={(_: React.ChangeEvent<unknown>, page: number) => {
              if (isMobile() && [1, 2, 3, 6, 7].includes(page)) {
                let siblingCount: number;
                switch (page) {
                  case 1:
                    siblingCount = 0;
                    break;
                  case 6:
                  case 7:
                    siblingCount = 1;
                    break;
                  default:
                    siblingCount = page % 2;
                    break;
                }
                setSiblingCount(siblingCount);
              }
              setPage(page);
              handleChangePage && handleChangePage(page);
            }}
            siblingCount={siblingCount}
          />
        </div>
      )}
    </>
  );
}
